"use client";

import { ResponsiveDropdownMenu } from "@repo/ui/components/responsive-dropdown-menu";
import { IconDotsVertical, IconPencil, IconTrash } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { Img } from "@/components/image";
import { AddonSellerName } from "@/features/addons/components/addon-seller-name";
import type { SelectedAddon } from "@/features/addons/lib/aggregate-selected-addons";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";

// Only the first few day ranges are shown as chips; the rest collapse into a
// "+N" counter to keep the item compact.
const VISIBLE_RANGES = 2;

const RANGE_CHIP_CLASS =
  "flex h-7.5 items-center rounded-lg border border-brand-grey2-300 bg-brand-white px-2 py-1.5 font-medium text-brand-black-100 text-xs leading-[18px]";

type DietAddonSelectedItemProps = {
  item: SelectedAddon;
  // Seller shown when the catalog names none (MPL-3832).
  cateringName?: string;
  onEdit: () => void;
  onRemove: () => void;
};

export const DietAddonSelectedItem = ({
  item,
  cateringName,
  onEdit,
  onRemove,
}: DietAddonSelectedItemProps) => {
  const t = useTranslations();
  const {
    addon,
    totalQuantity,
    unitPrice,
    originalPrice,
    hasPromo,
    depositTotal,
  } = item;

  // The unit price on the left is the addon price alone; the deposit is spelled
  // out on its own line below. The row total on the right is `productTotal`,
  // which already includes the deposits.

  const visibleRanges = item.dateRanges.slice(0, VISIBLE_RANGES);
  const hiddenRangesCount = item.dateRanges.length - visibleRanges.length;

  return (
    <div className="flex items-start gap-3 rounded-xl bg-brand-white p-4">
      <div className="relative size-12 shrink-0 overflow-hidden rounded-xl border border-brand-grey2-300">
        <Img
          alt={addon.name}
          className="size-full object-cover"
          height={48}
          src={addon.imageUrl ?? "/placeholder.svg"}
          width={48}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
        <div className="flex flex-col gap-1">
          <AddonSellerName fallback={cateringName} name={addon.merchantName} />
          <div className="flex items-start justify-between gap-3">
            <p className="min-w-0 truncate font-medium text-base text-brand-green1-300 leading-6">
              {addon.name}
            </p>
            <ResponsiveDropdownMenu
              align="end"
              drawerTitle={addon.name}
              items={[
                {
                  key: "edit",
                  label: t("cart.configuration.addons.summary.menu.edit"),
                  icon: <IconPencil className="size-5" stroke={2} />,
                  onSelect: onEdit,
                },
                {
                  key: "remove",
                  label: t("cart.configuration.addons.summary.menu.remove"),
                  icon: <IconTrash className="size-5" stroke={2} />,
                  onSelect: onRemove,
                  className: "text-brand-red-300",
                },
              ]}
              dropdownContentClassName="p-3 w-66"
              dropdownItemClassName="p-3 text-base hover:bg-brand-grey-light!"
              trigger={
                <button
                  aria-label={t(
                    "cart.configuration.addons.summary.menu.label",
                    {
                      name: addon.name,
                    },
                  )}
                  className="-mr-1 flex size-8 shrink-0 items-center justify-center rounded-lg text-brand-green1-300 hover:bg-brand-grey1"
                  type="button"
                >
                  <IconDotsVertical className="size-6" stroke={2} />
                </button>
              }
            />
          </div>

          {item.dateRanges.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {visibleRanges.map((range) => (
                <span className={RANGE_CHIP_CLASS} key={range}>
                  {range}
                </span>
              ))}
              {hiddenRangesCount > 0 && (
                <span className={RANGE_CHIP_CLASS}>
                  {t("cart.configuration.addons.summary.moreRanges", {
                    count: hiddenRangesCount,
                  })}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <div className="flex items-center justify-between gap-2">
            <span className="text-base text-brand-green1-300">
              {t.rich("cart.configuration.addons.summary.itemQuantity", {
                count: totalQuantity,
                mult: (chunks: ReactNode) => (
                  <span className="text-lg">{chunks}</span>
                ),
              })}{" "}
              <span
                className={cn(
                  "font-bold",
                  hasPromo ? "text-brand-red-300" : "text-brand-green1-300",
                )}
              >
                {getLocalePrice({ price: unitPrice })}
              </span>
              {/* Charged price first, the pre-discount one follows struck
                  through — same order as the addon card. */}
              {hasPromo && (
                <span className="ml-1 text-brand-black-100 text-sm leading-[22px] line-through">
                  {getLocalePrice({ price: originalPrice })}
                </span>
              )}
            </span>
            <span className="shrink-0 font-bold text-base text-brand-green1-300">
              {getLocalePrice({ price: item.productTotal })}
            </span>
          </div>

          {depositTotal > 0 && (
            <span className="text-brand-black-100 text-xs leading-[18px]">
              {t("cart.configuration.addons.summary.itemDeposit", {
                price: getLocalePrice({ price: depositTotal }),
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
